/**
 * StatusListItem - Status variant design
 *
 * Renders left-bordered list items with status badges.
 * Used internally by CList when variant="status".
 */
"use client";

import React from "react";
import { cn } from "@/lib/utils";
import type { StatusItemType } from "./types";
import { STATUS_CONFIG } from "./types";

interface StatusListItemProps {
  items: StatusItemType[];
  showDivider?: boolean;
}

const StatusListItem: React.FC<StatusListItemProps> = ({
  items,
  showDivider = true,
}) => {
  return (
    <div className={cn("flex flex-col", showDivider ? "gap-2" : "gap-0")}>
      {items.map((item, index) => {
        const config = STATUS_CONFIG[item.status];

        return (
          <div
            key={item.id || index}
            className={cn(
              "flex items-stretch bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden",
              item.onClick &&
                "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors",
            )}
            onClick={item.onClick}
          >
            {/* Status Border */}
            <div className={cn("w-1 flex-shrink-0", config.borderColor)} />

            {/* Content */}
            <div className="flex-1 p-3 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h4 className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {item.title}
                </h4>
                {item.subtitle && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                    {item.subtitle}
                  </p>
                )}
              </div>

              {/* Status Badge */}
              <span
                className={cn(
                  "text-[10px] font-semibold uppercase px-2 py-0.5 rounded ml-2 flex-shrink-0",
                  config.badgeClass,
                )}
              >
                {item.statusLabel}
              </span>
            </div>
          </div>
        );
      })}

      {items.length === 0 && (
        <div
          className={cn(
            "py-8 text-center text-sm",
            "text-slate-400 dark:text-gray-500",
          )}
        >
          No items to display
        </div>
      )}
    </div>
  );
};

export default StatusListItem;
